import { Box, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import FormikAutocomplete, { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { RaptorJobFormValues } from '../../../../RaptorJobValidationSchema';
import tid from '@/common/application/utils/tid';
import { buildConfirmedByOptions } from '../utils/buildConfirmedByOptions';
import { FormikDateTimePicker } from '@/common/presentation/components/forms/fields/FormikDateTimePicker';
import { useCustomerInfoSection } from '../../hook/useCustomerInfoSection';

interface CustomerInfoSectionProps {
  onAddNewCustomer: () => void;
  onCustomerChanged: () => void;
  setSelectedCustomerName?: (term: string) => void;
}

export function CustomerInfoSection({ onAddNewCustomer, onCustomerChanged, setSelectedCustomerName }: CustomerInfoSectionProps) {
  const {
    setCustomerSearchTerm,
    customerOptions,
    customerLoading,
    customerFilterOptions,
    customerCallerLoading,
    customerContacts,
    getContactAutocompleteOptions,
    getContactInitialOption,
    customerInitialOption,
    values
  } = useCustomerInfoSection();

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: '12px', fontWeight: 600 }}>
        Customer Information
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr' },
          gap: '12px',
          width: '100%'
        }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          id="customerId"
          label="Customer"
          options={customerOptions}
          optionsloading={customerLoading}
          filterOptions={customerFilterOptions}
          onInputChange={(_, inputValue) => setCustomerSearchTerm(inputValue)}
          getOptionLabel={(option: OptionType) => option?.label ?? 'Unknown Customer'}
          isOptionEqualToValue={(option: OptionType, value: OptionType) => option?.value === value?.value}
          initialOption={customerInitialOption}
          actions={[
            {
              label: 'Add New Customer',
              icon: <Add />,
              onClick: onAddNewCustomer
            }
          ]}
          required
          skipValidationOnActionClick
          onChange={(value) => {
            if (value && 'label' in value) {
              setSelectedCustomerName?.(value.label);
            }
            onCustomerChanged();
          }}
          sx={{ width: '100%' }}
          {...tid('jobs:create:customer-autocomplete')}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '12px',
          width: '100%',
          mt: 1
        }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          id="callerId"
          label="Caller ID"
          options={getContactAutocompleteOptions()}
          optionsloading={customerCallerLoading}
          filterOptions={(options, state) => {
            const input = state.inputValue.trim().toLowerCase();
            if (!input) return options;
            return options.filter((o) => o.label.toLowerCase().includes(input));
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
          placeholder={customerCallerLoading ? 'Loading contacts...' : 'Select a contact'}
          disabled={!values.customerId || customerCallerLoading || getContactAutocompleteOptions().length === 0}
          initialOption={getContactInitialOption()}
          sx={{ width: '100%', minWidth: 0 }}
          {...tid('jobs:create:caller-autocomplete')}
        />
        <FormikDateTimePicker<RaptorJobFormValues> id="calledAt" label="Called At" testId="jobs:create:called-at-input" />
      </Box>

      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '12px',
          width: '100%'
        }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          id="confirmedBy"
          label="Confirmed By"
          options={buildConfirmedByOptions(customerContacts)}
          optionsloading={customerCallerLoading}
          filterOptions={(options, state) => {
            const input = state.inputValue.trim().toLowerCase();
            if (!input) return options;
            return options.filter((o) => o.label.toLowerCase().includes(input));
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
          placeholder={customerCallerLoading ? 'Loading contacts...' : 'Select a contact'}
          disabled={!values.customerId || customerCallerLoading}
          sx={{ width: '100%', minWidth: 0 }}
          {...tid('jobs:create:confirmed-by-autocomplete')}
        />
        <FormikDateTimePicker<RaptorJobFormValues> id="confirmedAt" label="Confirmed At" testId="jobs:create:confirmed-at-input" />
      </Box>
    </>
  );
}
